import { useEffect, useState } from "react";
import { toast } from "sonner";
import { partnerAdminApi, bpsToPct, centsToUsd } from "@/lib/partner";

const STATUSES = ["all", "pending", "approved", "payable", "paid", "reversed"];

export default function PartnerCommissionsAdmin() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = { limit: 100, offset: 0 };
    if (status !== "all") params.status = status;
    try {
      const d = await partnerAdminApi.commissions(params);
      setItems(d.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [status]);

  const approve = async (id) => {
    try { await partnerAdminApi.approveCommission(id, "admin_approved"); toast.success("Approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reverse = async (id) => {
    const reason = window.prompt("Reason for reversal (e.g., fraud, admin_correction):", "manual");
    if (!reason) return;
    try {
      const r = await partnerAdminApi.reverseCommission(id, reason);
      toast.success(r.mode === "clawback" ? "Clawback row inserted (original preserved)" : "Reversed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-partner-commissions">
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`admin-comm-filter-${s}`}
            className={`rounded-full border px-3 py-1.5 text-xs capitalize transition-colors ${
              status === s ? "border-[#D6A653] bg-[#D6A653]/12 text-[#D6A653]" : "border-white/12 text-white/55 hover:text-white"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? <p className="text-white/50">Loading…</p> : items.length === 0 ? <p className="text-white/40">No commissions.</p> : (
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0B0D12]">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-white/40">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Partner</th>
                <th className="px-4 py-3">Workspace</th>
                <th className="px-4 py-3 text-right">Amount paid</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3 text-right">Commission</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="text-white/80">
              {items.map((c) => (
                <tr key={c.id} className="border-t border-white/5" data-testid={`admin-comm-${c.id}`}>
                  <td className="px-4 py-3 text-xs text-white/50">{new Date(c.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#D6A653]">{c.partner_id.slice(0, 10)}…</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-white/60">{c.workspace_id.slice(0, 10)}…</td>
                  <td className="px-4 py-3 text-right tabular-nums">{centsToUsd(c.amount_paid_cents, c.currency)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{bpsToPct(c.commission_rate_bps)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums ${c.commission_cents < 0 ? "text-red-300" : ""}`}>{centsToUsd(c.commission_cents, c.currency)}</td>
                  <td className="px-4 py-3 capitalize">{c.status}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {c.status === "pending" ? (
                        <button data-testid={`approve-comm-${c.id}`} onClick={() => approve(c.id)} className="rounded-lg border border-emerald-500/40 px-2.5 py-1 text-xs text-emerald-300 hover:bg-emerald-500/10">Approve</button>
                      ) : null}
                      {["pending", "approved", "payable", "paid"].includes(c.status) ? (
                        <button data-testid={`reverse-comm-${c.id}`} onClick={() => reverse(c.id)} className="rounded-lg border border-red-500/40 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/10">Reverse</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
